import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/apiClient";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { CountUp } from "@/components/CountUp";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, BadgeCheck, Building2, Loader2, MapPin, Search } from "lucide-react";

// Public (no-login) page an MR opens on their phone to check their own
// tally: Emp Code in, then Name / Division / HQ / pledges enrolled.
// Built mobile-first — the link is shared with the field team over WhatsApp.
export default function MrLookupPage() {
  const [empCode, setEmpCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const lookup = async (e) => {
    e?.preventDefault();
    const code = empCode.trim();
    if (!code) { toast.error("Enter your Employee ID."); return; }
    setLoading(true);
    setResult(null);
    try {
      const res = await api.get(`/mr/lookup/${encodeURIComponent(code)}`);
      setResult(res.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "No employee found for this ID.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="App bg-slate-50 min-h-screen flex flex-col">
      <Navbar />
      <div className="flex-1 w-full max-w-md mx-auto px-4 py-8 sm:py-14">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#0056D2] mb-5"
          data-testid="mr-lookup-back-link"
        >
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>

        <form onSubmit={lookup} className="rounded-3xl bg-white border border-slate-200 p-6 sm:p-8">
          <div className="grid place-items-center w-14 h-14 rounded-full bg-blue-50 mx-auto mb-4">
            <Search className="w-7 h-7 text-[#0056D2]" />
          </div>
          <h1 className="font-display font-extrabold text-2xl sm:text-3xl text-slate-900 text-center">
            My Pledge Count
          </h1>
          <p className="mt-2.5 text-sm text-slate-500 leading-relaxed text-center">
            Enter your Employee ID to see how many doctor pledges are enrolled against your name.
          </p>

          <div className="mt-7 text-left">
            <Label htmlFor="mr-emp" className="text-sm font-medium text-slate-700">Employee ID</Label>
            <Input
              data-testid="mr-lookup-input"
              id="mr-emp"
              value={empCode}
              onChange={(e) => setEmpCode(e.target.value.replace(/\D/g, "").slice(0, 20))}
              // Emp Codes are numeric, so ask phones for the number pad.
              type="tel"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="off"
              enterKeyHint="search"
              className="mt-1.5 h-14 text-center text-xl font-mono tracking-[0.2em]"
              placeholder="e.g. 14439"
            />
          </div>

          <Button
            data-testid="mr-lookup-btn"
            type="submit"
            disabled={loading}
            className="mt-5 w-full rounded-full bg-[#0056D2] hover:bg-blue-700 text-white font-semibold h-14 text-base"
          >
            {loading
              ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Checking…</>
              : <><Search className="w-5 h-5 mr-2" /> View My Details</>}
          </Button>
        </form>

        {result && (
          <div
            data-testid="mr-lookup-result"
            className="mt-6 rounded-3xl bg-white border border-slate-200 overflow-hidden"
          >
            <div className="bg-slate-900 px-6 py-5 text-center">
              <p className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">
                Employee ID {result.emp_code}
              </p>
              <p className="font-display font-extrabold text-xl text-white mt-1.5 leading-snug">
                {result.name}
              </p>
            </div>

            <div className="grid grid-cols-2 divide-x divide-slate-200 border-b border-slate-200">
              <div className="px-5 py-4">
                <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                  <Building2 className="w-3.5 h-3.5" /> Division
                </p>
                <p className="mt-1 font-semibold text-slate-900 break-words">{result.division || "—"}</p>
              </div>
              <div className="px-5 py-4">
                <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                  <MapPin className="w-3.5 h-3.5" /> HQ
                </p>
                <p className="mt-1 font-semibold text-slate-900 break-words">{result.hq || "—"}</p>
              </div>
            </div>

            <div className="px-6 py-7 text-center bg-blue-50/60">
              <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                Pledges Enrolled
              </p>
              <CountUp
                value={result.pledge_count}
                className="block font-display font-extrabold text-6xl text-[#0056D2] mt-1.5 tabular-nums"
              />
              {result.pledge_count === 0 && (
                <p className="mt-2.5 text-sm text-slate-500">
                  No pledges enrolled yet — your first one is waiting.
                </p>
              )}
              {result.pledge_count > 0 && (
                <p className="mt-2.5 inline-flex items-center gap-1.5 text-sm font-medium text-[#138808]">
                  <BadgeCheck className="w-4 h-4" /> Thank you for building a Healthy India
                </p>
              )}
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
